/**
 * @file AdminPanel.component.jsx
 * @description Admin panel for USABG University management
 *
 * This component provides comprehensive admin functionality for managing
 * the university training program. Only visible to admin users.
 *
 * Features:
 * - **Sessions Tab**:
 *   - Create/edit/delete training sessions
 *   - Assign location and host
 *   - Set capacity limits
 *   - View enrolled count
 *   - Expandable enrollee management per session
 *
 * - **Locations Tab**:
 *   - Create/edit/delete training locations
 *   - Full address management
 *   - Assign location owner
 *
 * - **Enrollee Management** (within expanded session):
 *   - View all enrollees with status
 *   - Check-in enrollees (initiate)
 *   - Mark as completed
 *   - Cancel with optional refund
 *   - Bulk actions (check-in, complete)
 *
 * @see /docs/modules/UsabgUniversity/README.md for full documentation
 */
import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import {
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBBtn,
  MDBTabs,
  MDBTabsItem,
  MDBTabsLink,
  MDBTabsPane,
  MDBTabsContent,
  MDBTable,
  MDBTableHead,
  MDBTableBody,
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBModalFooter,
  MDBSpinner,
  MDBBadge,
  MDBRow,
  MDBCol,
  MDBCollapse,
  MDBModalDialog,
  MDBModalContent,
} from 'mdb-react-ui-kit'
import { format, parseISO } from 'date-fns'
import UniversityStore, { isSessionEnded } from '../../store/University.store'
import UserProfileService from '../../../../shared/services/UserProfile.service'
import UIInput from '../../../../components/forms/Input/UIInput.component'
import UISelect from '../../../../components/forms/Select/UISelect.component'
import UITypeAhead from '../../../../components/forms/TypeAhead/UITypeAhead.component'
import UIDatePickerInput from '../../../../components/forms/DatePicker/UIDatePickerInput.component'
import UIInputCheckbox from '../../../../components/forms/InputCheckbox/UIInputCheckbox.component'
import UIInputToggleSwitch from '../../../../components/forms/InputToggleSwitch/UIInputToggleSwitch.component'

import { toast } from 'react-toastify'
import './AdminPanel.component.scss'

/**
 * AdminPanel Component
 *
 * Admin interface for managing USABG University locations and sessions.
 * Only rendered for users with admin privileges (internal-staff, internal-admin, system-admin).
 *
 * @component
 * @example
 * <AdminPanel />
 */
const AdminPanel = observer(() => {
  const [activeTab, setActiveTab] = useState('sessions')
  const [isAdmin, setIsAdmin] = useState(false)

  // Location modal state
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [editingLocation, setEditingLocation] = useState(null)
  const [locationForm, setLocationForm] = useState({
    name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    owner_id: '',
  })
  const [locationLoading, setLocationLoading] = useState(false)
  const [locationError, setLocationError] = useState(null)

  // Owner search state
  const [ownerSearchQuery, setOwnerSearchQuery] = useState('')
  const [ownerSearchResults, setOwnerSearchResults] = useState([])
  const [isSearchingOwner, setIsSearchingOwner] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState(null)

  // Host search state
  const [hostSearchQuery, setHostSearchQuery] = useState('')
  const [hostSearchResults, setHostSearchResults] = useState([])
  const [isSearchingHost, setIsSearchingHost] = useState(false)
  const [selectedHost, setSelectedHost] = useState(null)

  // Session modal state
  const [showSessionModal, setShowSessionModal] = useState(false)
  const [editingSession, setEditingSession] = useState(null)
  const [sessionForm, setSessionForm] = useState({
    location_id: '',
    host_id: '',
    start_date: null,
    end_date: null,
    max_capacity: '',
  })
  const [sessionLoading, setSessionLoading] = useState(false)
  const [sessionError, setSessionError] = useState(null)

  // Session delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingSession, setDeletingSession] = useState(null)
  const [deleteEnrollmentCount, setDeleteEnrollmentCount] = useState({
    total: 0,
    paid: 0,
  })
  const [issueRefunds, setIssueRefunds] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Enrollee management state
  const [selectedEnrollees, setSelectedEnrollees] = useState([])
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancellingEnrollment, setCancellingEnrollment] = useState(null)
  const [issueRefundOnCancel, setIssueRefundOnCancel] = useState(false)
  const [enrolleeActionLoading, setEnrolleeActionLoading] = useState({})
  const [exportingSessionId, setExportingSessionId] = useState(null)

  useEffect(() => {
    const adminCheck = UserProfileService.isA(['internal-staff', 'internal-admin', 'system-admin'])
    setIsAdmin(adminCheck)

    if (adminCheck) {
      UniversityStore.fetchLocations()
      UniversityStore.fetchSessions()
    }
  }, [])

  // Search for owners when query changes
  useEffect(() => {
    const searchOwners = async () => {
      if (ownerSearchQuery.length < 2) {
        setOwnerSearchResults([])
        return
      }

      setIsSearchingOwner(true)
      const users = await UniversityStore.searchUsers(ownerSearchQuery)
      const mappedUsers = users
        .filter(u => u.u_active == null || parseInt(u.u_active) >= 1)
        .map(u => ({
          value: u.id,
          text: u.u_fullname || `${u.u_fname || ''} ${u.u_lname || ''}`.trim(),
          checked: u.id === locationForm.owner_id,
        }))
      setOwnerSearchResults(mappedUsers)
      setIsSearchingOwner(false)
    }

    const debounce = setTimeout(searchOwners, 300)
    return () => clearTimeout(debounce)
  }, [ownerSearchQuery, locationForm.owner_id])

  // Search for hosts when query changes
  useEffect(() => {
    const searchHosts = async () => {
      if (hostSearchQuery.length < 2) {
        setHostSearchResults([])
        return
      }

      setIsSearchingHost(true)
      const users = await UniversityStore.searchUsers(hostSearchQuery)
      const mappedUsers = users
        .filter(u => u.u_active == null || parseInt(u.u_active) >= 1)
        .map(u => ({
          value: u.id,
          text: `${u.u_fullname || `${u.u_fname || ''} ${u.u_lname || ''}`.trim()} (${u.u_email || ''})`,
          checked: u.id === sessionForm.host_id,
        }))
      setHostSearchResults(mappedUsers)
      setIsSearchingHost(false)
    }

    const debounce = setTimeout(searchHosts, 300)
    return () => clearTimeout(debounce)
  }, [hostSearchQuery, sessionForm.host_id])

  // Don't render if not admin
  if (!isAdmin) {
    return null
  }

  const formatDateRange = (startDate, endDate) => {
    try {
      // Extract just YYYY-MM-DD portion and parse as local date to prevent timezone rollover
      const toLocalDate = dateStr => {
        const dateOnly = String(dateStr).split('T')[0].split(' ')[0]
        const [year, month, day] = dateOnly.split('-').map(Number)
        return new Date(year, month - 1, day)
      }
      const start = toLocalDate(startDate)
      const end = toLocalDate(endDate)
      return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`
    } catch {
      return `${startDate} - ${endDate}`
    }
  }

  const formatFullAddress = location => {
    const parts = [
      location.address_line_1,
      location.address_line_2,
      location.city,
      location.state,
      location.zip_code,
    ].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : '-'
  }

  const getOwnerDisplayName = location => {
    if (location.owner) {
      return (
        location.owner.u_fullname ||
        `${location.owner.u_fname || ''} ${location.owner.u_lname || ''}`.trim() ||
        location.owner.u_login
      )
    }
    return '-'
  }

  // ==================== Location Handlers ====================

  const handleOpenLocationModal = (location = null) => {
    if (location) {
      setEditingLocation(location)
      setLocationForm({
        name: location.name || '',
        address_line_1: location.address_line_1 || '',
        address_line_2: location.address_line_2 || '',
        city: location.city || '',
        state: location.state || '',
        zip_code: location.zip_code || '',
        phone: location.phone || '',
        owner_id: location.owner_id || '',
      })
      if (location.owner) {
        const ownerName =
          location.owner.u_fullname || `${location.owner.u_fname || ''} ${location.owner.u_lname || ''}`.trim()
        setSelectedOwner({
          value: location.owner.id,
          text: ownerName,
          checked: true,
        })
        setOwnerSearchResults([
          {
            value: location.owner.id,
            text: ownerName,
            checked: true,
          },
        ])
      } else {
        setSelectedOwner(null)
        setOwnerSearchResults([])
      }
    } else {
      setEditingLocation(null)
      setLocationForm({
        name: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        owner_id: '',
      })
      setSelectedOwner(null)
      setOwnerSearchResults([])
    }
    setOwnerSearchQuery('')
    setLocationError(null)
    setShowLocationModal(true)
  }

  const handleCloseLocationModal = () => {
    setShowLocationModal(false)
    setEditingLocation(null)
    setLocationError(null)
    setSelectedOwner(null)
    setOwnerSearchResults([])
    setOwnerSearchQuery('')
  }

  const handleOwnerSelect = option => {
    if (option && option.value) {
      setSelectedOwner(option)
      setLocationForm({ ...locationForm, owner_id: option.value })
      setOwnerSearchResults(
        ownerSearchResults.map(o => ({
          ...o,
          checked: o.value === option.value,
        }))
      )
    } else {
      setSelectedOwner(null)
      setLocationForm({ ...locationForm, owner_id: '' })
    }
  }

  const handleHostSelect = option => {
    if (option && option.value) {
      setSelectedHost(option)
      setSessionForm({ ...sessionForm, host_id: option.value })
    } else {
      setSelectedHost(null)
      setSessionForm({ ...sessionForm, host_id: '' })
    }
  }

  const handleSaveLocation = async () => {
    // Validate required fields
    const requiredFields = [
      { field: 'name', label: 'Location Name' },
      { field: 'address_line_1', label: 'Street Address' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'zip_code', label: 'ZIP Code' },
      { field: 'owner_id', label: 'Owner' },
    ]

    for (const { field, label } of requiredFields) {
      if (!locationForm[field]) {
        setLocationError(`${label} is required`)
        return
      }
    }

    setLocationLoading(true)
    setLocationError(null)

    let result
    if (editingLocation) {
      result = await UniversityStore.updateLocation(editingLocation.id, locationForm)
    } else {
      result = await UniversityStore.createLocation(locationForm)
    }

    setLocationLoading(false)

    if (result.success) {
      handleCloseLocationModal()
    } else {
      setLocationError(result.error)
    }
  }

  const handleDeleteLocation = async location => {
    if (!window.confirm(`Are you sure you want to delete "${location.name}"?`)) {
      return
    }

    await UniversityStore.deleteLocation(location.id)
  }

  // ==================== Session Handlers ====================

  // Helper to parse date string as local date (prevents timezone rollover)
  const toLocalDate = dateStr => {
    if (!dateStr) return null
    const dateOnly = String(dateStr).split('T')[0].split(' ')[0]
    const [year, month, day] = dateOnly.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  const handleOpenSessionModal = (session = null) => {
    if (session) {
      setEditingSession(session)
      setSessionForm({
        location_id: session.location_id || '',
        host_id: session.host_id || '',
        start_date: toLocalDate(session.start_date),
        end_date: toLocalDate(session.end_date),
        max_capacity: session.max_capacity || '',
      })
      // Pre-populate host selector if session has host
      if (session.host) {
        setSelectedHost({
          value: session.host.id,
          text: `${session.host.name} (${session.host.email || ''})`,
        })
      } else {
        setSelectedHost(null)
      }
      setHostSearchQuery('')
    } else {
      setEditingSession(null)
      setSessionForm({
        location_id: '',
        host_id: '',
        start_date: null,
        end_date: null,
        max_capacity: '',
      })
      setSelectedHost(null)
      setHostSearchQuery('')
    }
    setSessionError(null)
    setShowSessionModal(true)
  }

  const handleCloseSessionModal = () => {
    setShowSessionModal(false)
    setEditingSession(null)
    setSessionError(null)
  }

  const handleSaveSession = async () => {
    if (!sessionForm.location_id || !sessionForm.start_date || !sessionForm.end_date) {
      setSessionError('Location, start date, and end date are required')
      return
    }

    setSessionLoading(true)
    setSessionError(null)

    const data = {
      location_id: sessionForm.location_id,
      host_id: sessionForm.host_id || null,
      start_date: sessionForm.start_date ? format(sessionForm.start_date, 'yyyy-MM-dd') : null,
      end_date: sessionForm.end_date ? format(sessionForm.end_date, 'yyyy-MM-dd') : null,
      max_capacity: sessionForm.max_capacity ? parseInt(sessionForm.max_capacity) : null,
    }

    let result
    if (editingSession) {
      result = await UniversityStore.updateSession(editingSession.id, data)
    } else {
      result = await UniversityStore.createSession(data)
    }

    setSessionLoading(false)

    if (result.success) {
      handleCloseSessionModal()
    } else {
      setSessionError(result.error)
    }
  }

  const handleOpenDeleteModal = async session => {
    setDeletingSession(session)
    setIssueRefunds(false)
    setDeleteLoading(false)

    // Fetch enrollment count
    const counts = await UniversityStore.getEnrollmentCount(session.id)
    setDeleteEnrollmentCount(counts)
    setShowDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false)
    setDeletingSession(null)
    setDeleteEnrollmentCount({ total: 0, paid: 0 })
  }

  const handleConfirmDelete = async () => {
    if (!deletingSession) return

    setDeleteLoading(true)
    const result = await UniversityStore.deleteSession(deletingSession.id, issueRefunds)
    setDeleteLoading(false)

    if (result.success) {
      handleCloseDeleteModal()
    } else {
      alert(result.error || 'Failed to delete session')
    }
  }

  const handleExportSession = async session => {
    setExportingSessionId(session.id)
    const result = await UniversityStore.exportSessionAttendees(session)

    if (result.success) {
      toast.success('Attendees exported successfully.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } else {
      toast.error(result.error || 'Export failed.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    }

    setExportingSessionId(null)
  }

  // Location select options for session form
  const locationOptions = UniversityStore.locations.map(loc => ({
    value: `${loc.id}`,
    text: loc.name,
    checked: `${loc.id}` === `${sessionForm.location_id}`,
  }))

  // State options for address form
  const stateOptions = [
    { value: '', text: '-- Select State --', checked: !locationForm.state },
    { value: 'AL', text: 'Alabama', checked: locationForm.state === 'AL' },
    { value: 'AK', text: 'Alaska', checked: locationForm.state === 'AK' },
    { value: 'AZ', text: 'Arizona', checked: locationForm.state === 'AZ' },
    { value: 'AR', text: 'Arkansas', checked: locationForm.state === 'AR' },
    { value: 'CA', text: 'California', checked: locationForm.state === 'CA' },
    { value: 'CO', text: 'Colorado', checked: locationForm.state === 'CO' },
    { value: 'CT', text: 'Connecticut', checked: locationForm.state === 'CT' },
    { value: 'DE', text: 'Delaware', checked: locationForm.state === 'DE' },
    { value: 'FL', text: 'Florida', checked: locationForm.state === 'FL' },
    { value: 'GA', text: 'Georgia', checked: locationForm.state === 'GA' },
    { value: 'HI', text: 'Hawaii', checked: locationForm.state === 'HI' },
    { value: 'ID', text: 'Idaho', checked: locationForm.state === 'ID' },
    { value: 'IL', text: 'Illinois', checked: locationForm.state === 'IL' },
    { value: 'IN', text: 'Indiana', checked: locationForm.state === 'IN' },
    { value: 'IA', text: 'Iowa', checked: locationForm.state === 'IA' },
    { value: 'KS', text: 'Kansas', checked: locationForm.state === 'KS' },
    { value: 'KY', text: 'Kentucky', checked: locationForm.state === 'KY' },
    { value: 'LA', text: 'Louisiana', checked: locationForm.state === 'LA' },
    { value: 'ME', text: 'Maine', checked: locationForm.state === 'ME' },
    { value: 'MD', text: 'Maryland', checked: locationForm.state === 'MD' },
    {
      value: 'MA',
      text: 'Massachusetts',
      checked: locationForm.state === 'MA',
    },
    { value: 'MI', text: 'Michigan', checked: locationForm.state === 'MI' },
    { value: 'MN', text: 'Minnesota', checked: locationForm.state === 'MN' },
    { value: 'MS', text: 'Mississippi', checked: locationForm.state === 'MS' },
    { value: 'MO', text: 'Missouri', checked: locationForm.state === 'MO' },
    { value: 'MT', text: 'Montana', checked: locationForm.state === 'MT' },
    { value: 'NE', text: 'Nebraska', checked: locationForm.state === 'NE' },
    { value: 'NV', text: 'Nevada', checked: locationForm.state === 'NV' },
    {
      value: 'NH',
      text: 'New Hampshire',
      checked: locationForm.state === 'NH',
    },
    { value: 'NJ', text: 'New Jersey', checked: locationForm.state === 'NJ' },
    { value: 'NM', text: 'New Mexico', checked: locationForm.state === 'NM' },
    { value: 'NY', text: 'New York', checked: locationForm.state === 'NY' },
    {
      value: 'NC',
      text: 'North Carolina',
      checked: locationForm.state === 'NC',
    },
    { value: 'ND', text: 'North Dakota', checked: locationForm.state === 'ND' },
    { value: 'OH', text: 'Ohio', checked: locationForm.state === 'OH' },
    { value: 'OK', text: 'Oklahoma', checked: locationForm.state === 'OK' },
    { value: 'OR', text: 'Oregon', checked: locationForm.state === 'OR' },
    { value: 'PA', text: 'Pennsylvania', checked: locationForm.state === 'PA' },
    { value: 'RI', text: 'Rhode Island', checked: locationForm.state === 'RI' },
    {
      value: 'SC',
      text: 'South Carolina',
      checked: locationForm.state === 'SC',
    },
    { value: 'SD', text: 'South Dakota', checked: locationForm.state === 'SD' },
    { value: 'TN', text: 'Tennessee', checked: locationForm.state === 'TN' },
    { value: 'TX', text: 'Texas', checked: locationForm.state === 'TX' },
    { value: 'UT', text: 'Utah', checked: locationForm.state === 'UT' },
    { value: 'VT', text: 'Vermont', checked: locationForm.state === 'VT' },
    { value: 'VA', text: 'Virginia', checked: locationForm.state === 'VA' },
    { value: 'WA', text: 'Washington', checked: locationForm.state === 'WA' },
    {
      value: 'WV',
      text: 'West Virginia',
      checked: locationForm.state === 'WV',
    },
    { value: 'WI', text: 'Wisconsin', checked: locationForm.state === 'WI' },
    { value: 'WY', text: 'Wyoming', checked: locationForm.state === 'WY' },
  ]

  // ==================== Enrollee Management ====================

  const getEnrolleeStatus = enrollee => {
    if (enrollee.completed_at) return { label: 'Completed', color: 'success', icon: 'award' }
    if (enrollee.initiated_at) return { label: 'Checked In', color: 'info', icon: 'door-open' }
    if (enrollee.paid_at && !enrollee.refunded_at) return { label: 'Paid', color: 'success', icon: 'check' }
    if (enrollee.refunded_at) return { label: 'Refunded', color: 'warning', icon: 'undo' }
    if (enrollee.accepted_at) return { label: 'Accepted', color: 'primary', icon: 'thumbs-up' }
    if (enrollee.invited_at) return { label: 'Invited', color: 'warning', icon: 'envelope' }
    return { label: 'Enrolled', color: 'secondary', icon: 'user' }
  }

  const handleInitiateEnrollee = async (enrollee, sessionId) => {
    setEnrolleeActionLoading({ ...enrolleeActionLoading, [enrollee.id]: true })
    await UniversityStore.initiateEnrollment(enrollee.id, sessionId)
    setEnrolleeActionLoading({ ...enrolleeActionLoading, [enrollee.id]: false })
  }

  const handleCompleteEnrollee = async (enrollee, sessionId) => {
    setEnrolleeActionLoading({ ...enrolleeActionLoading, [enrollee.id]: true })
    await UniversityStore.completeEnrollment(enrollee.id, sessionId)
    setEnrolleeActionLoading({ ...enrolleeActionLoading, [enrollee.id]: false })
  }

  const handleOpenCancelModal = enrollee => {
    setCancellingEnrollment(enrollee)
    setIssueRefundOnCancel(false)
    setCancelModalOpen(true)
  }

  const handleConfirmCancel = async () => {
    if (!cancellingEnrollment) return

    const sessionId = cancellingEnrollment.session_id
    setEnrolleeActionLoading({
      ...enrolleeActionLoading,
      [cancellingEnrollment.id]: true,
    })

    await UniversityStore.cancelEnrollment(cancellingEnrollment.id, sessionId, issueRefundOnCancel)

    setEnrolleeActionLoading({
      ...enrolleeActionLoading,
      [cancellingEnrollment.id]: false,
    })
    setCancelModalOpen(false)
    setCancellingEnrollment(null)
  }

  /**
   * Handle changing an enrollee's session via the dropdown.
   * Only available when initiated_at is NULL (not yet checked in).
   * Shows success/error feedback to the user.
   * Resets the select element after the action to prevent re-trigger on re-render.
   */
  const handleChangeSession = async (enrollee, currentSessionId, targetSessionId, selectElement) => {
    if (!targetSessionId) return

    // Reset select immediately to prevent re-trigger during store refresh
    if (selectElement) selectElement.value = ''

    // Find target session details for the success message
    const targetSession = UniversityStore.sessions.find(s => `${s.id}` === `${targetSessionId}`)
    const targetLabel = targetSession
      ? formatDateRange(targetSession.start_date, targetSession.end_date)
      : `Session #${targetSessionId}`

    setEnrolleeActionLoading({ ...enrolleeActionLoading, [enrollee.id]: true })
    const result = await UniversityStore.changeEnrollmentSession(enrollee.id, currentSessionId, targetSessionId)
    setEnrolleeActionLoading({ ...enrolleeActionLoading, [enrollee.id]: false })

    if (result.success) {
      toast.success(`${enrollee.user?.name || 'Attendee'} moved to ${targetLabel} successfully.`, {
        position: toast.POSITION.TOP_RIGHT,
      })
    } else {
      toast.error(result.error, { position: toast.POSITION.TOP_RIGHT })
    }
  }

  const handleBulkCheckIn = async session => {
    const enrollees = UniversityStore.sessionEnrollments[session.id] || []
    const validIds = selectedEnrollees.filter(id => {
      const e = enrollees.find(en => en.id === id)
      return e && e.paid_at && !e.initiated_at && !e.refunded_at
    })
    if (validIds.length === 0) return
    await UniversityStore.bulkInitiateEnrollments(validIds, session.id)
    setSelectedEnrollees([])
  }

  const handleBulkComplete = async session => {
    const enrollees = UniversityStore.sessionEnrollments[session.id] || []
    const validIds = selectedEnrollees.filter(id => {
      const e = enrollees.find(en => en.id === id)
      return e && e.initiated_at && !e.completed_at
    })
    if (validIds.length === 0) return
    await UniversityStore.bulkCompleteEnrollments(validIds, session.id)
    setSelectedEnrollees([])
  }

  const handleSelectAll = (checked, paidEnrollees) => {
    if (checked) {
      setSelectedEnrollees(paidEnrollees.map(e => e.id))
    } else {
      setSelectedEnrollees([])
    }
  }

  const handleSelectEnrollee = (enrolleeId, checked) => {
    if (checked) {
      setSelectedEnrollees([...selectedEnrollees, enrolleeId])
    } else {
      setSelectedEnrollees(selectedEnrollees.filter(id => id !== enrolleeId))
    }
  }

  const renderEnrolleesTable = session => {
    const enrollees = UniversityStore.sessionEnrollments[session.id] || []
    const paidEnrollees = enrollees.filter(e => e.paid_at && !e.refunded_at)

    if (UniversityStore.isLoading.sessionEnrollments) {
      return (
        <div className='text-center py-4'>
          <MDBSpinner small />
          <p className='mt-2 mb-0'>Loading enrollees...</p>
        </div>
      )
    }

    return (
      <div className='enrollees-container'>
        {/* Bulk Actions Bar */}
        {selectedEnrollees.length > 0 && (
          <div className='bulk-actions-bar'>
            <span>{selectedEnrollees.length} selected</span>
            <MDBBtn type='button' size='sm' color='info' onClick={() => handleBulkCheckIn(session)}>
              <i className='fas fa-door-open mr-1'></i> Check-In
            </MDBBtn>
            <MDBBtn type='button' size='sm' color='success' onClick={() => handleBulkComplete(session)}>
              <i className='fas fa-award mr-1'></i> Complete
            </MDBBtn>
            <MDBBtn type='button' size='sm' color='secondary' onClick={() => setSelectedEnrollees([])}>
              Clear Selection
            </MDBBtn>
          </div>
        )}

        {enrollees.length === 0 ? (
          <p className='text-muted text-center py-3 mb-0'>No enrollees for this session.</p>
        ) : (
          <MDBTable small responsive className='enrollees-table mb-0'>
            <MDBTableHead>
              <tr>
                <th className='checkbox-col'>
                  <UIInputCheckbox
                    id={`select-all-${session.id}`}
                    name='select_all'
                    label=' '
                    checked={selectedEnrollees.length === paidEnrollees.length && paidEnrollees.length > 0}
                    onChange={() =>
                      handleSelectAll(
                        !(selectedEnrollees.length === paidEnrollees.length && paidEnrollees.length > 0),
                        paidEnrollees
                      )
                    }
                    disabled={paidEnrollees.length === 0}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Change Session</th>
                <th>Actions</th>
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {enrollees.map(enrollee => {
                const status = getEnrolleeStatus(enrollee)
                const canCheckIn = enrollee.paid_at && !enrollee.initiated_at && !enrollee.refunded_at
                const canComplete = enrollee.initiated_at && !enrollee.completed_at
                const canRefund = enrollee.paid_at && !enrollee.refunded_at && !enrollee.completed_at
                const isPaid = enrollee.paid_at && !enrollee.refunded_at

                return (
                  <tr key={enrollee.id}>
                    <td className='checkbox-col'>
                      <UIInputCheckbox
                        id={`select-enrollee-${enrollee.id}`}
                        name={`select_enrollee_${enrollee.id}`}
                        label=' '
                        checked={selectedEnrollees.includes(enrollee.id)}
                        onChange={() => handleSelectEnrollee(enrollee.id, !selectedEnrollees.includes(enrollee.id))}
                        disabled={!isPaid}
                      />
                    </td>
                    <td>{enrollee.user?.name || 'Unknown'}</td>
                    <td>{enrollee.user?.email || '-'}</td>
                    <td>
                      <MDBBadge color={status.color}>
                        <i className={`fas fa-${status.icon} mr-1`}></i>
                        {status.label}
                      </MDBBadge>
                    </td>
                    <td>
                      {/* Dropdown only shown when attendee has NOT been checked in or completed */}
                      {!enrollee.initiated_at && !enrollee.completed_at ? (
                        <select
                          className='form-control form-control-sm'
                          id={`change-session-${enrollee.id}`}
                          disabled={enrolleeActionLoading[enrollee.id]}
                          defaultValue=''
                          onChange={e => {
                            if (e.target.value) {
                              handleChangeSession(enrollee, session.id, e.target.value, e.target)
                            }
                          }}
                        >
                          <option value=''>-- Select Session --</option>
                          {UniversityStore.sessions
                            .filter(s => s.id !== session.id)
                            .map(s => (
                              <option key={s.id} value={s.id}>
                                {formatDateRange(s.start_date, s.end_date)} - {s.location?.name || 'TBD'}
                              </option>
                            ))}
                        </select>
                      ) : (
                        <span className='text-muted'>N/A</span>
                      )}
                    </td>
                    <td className='enrollee-actions'>
                      {canCheckIn && (
                        <MDBBtn
                          type='button'
                          size='sm'
                          color='info'
                          className='mr-1'
                          disabled={enrolleeActionLoading[enrollee.id]}
                          onClick={() => handleInitiateEnrollee(enrollee, session.id)}
                          title='Check In'
                        >
                          <i className='fas fa-door-open mr-1'></i>
                          Check In
                        </MDBBtn>
                      )}
                      {canComplete && (
                        <MDBBtn
                          type='button'
                          size='sm'
                          color='success'
                          className='mr-1'
                          disabled={enrolleeActionLoading[enrollee.id]}
                          onClick={() => handleCompleteEnrollee(enrollee, session.id)}
                          title='Mark Complete'
                        >
                          <i className='fas fa-award mr-1'></i>
                          Complete
                        </MDBBtn>
                      )}
                      <MDBBtn
                        type='button'
                        size='sm'
                        color='danger'
                        disabled={enrolleeActionLoading[enrollee.id]}
                        onClick={() => handleOpenCancelModal(enrollee)}
                        title={canRefund ? 'Cancel & Refund' : 'Cancel'}
                      >
                        <i className='fas fa-times mr-1'></i>
                        Cancel
                      </MDBBtn>
                    </td>
                  </tr>
                )
              })}
            </MDBTableBody>
          </MDBTable>
        )}
      </div>
    )
  }

  // ==================== Render ====================

  return (
    <MDBCard id='UniversityAdminPanelComponent'>
      <MDBCardHeader className='admin-header'>
        <i className='fas fa-cog mr-2'></i>
        Admin Panel
        <MDBBadge color='danger' className='ml-2'>
          Admin Only
        </MDBBadge>
      </MDBCardHeader>
      <MDBCardBody>
        {/* Tab Navigation with Add Button */}
        <div className='tab-header'>
          <MDBTabs className='nav-tabs'>
            <MDBTabsItem>
              <span
                className={`nav-link ${activeTab === 'sessions' ? 'active' : ''}`}
                onClick={() => setActiveTab('sessions')}
                role='button'
              >
                <i className='fas fa-calendar-alt mr-1'></i>
                Sessions
              </span>
            </MDBTabsItem>
            <MDBTabsItem>
              <span
                className={`nav-link ${activeTab === 'locations' ? 'active' : ''}`}
                onClick={() => setActiveTab('locations')}
                role='button'
              >
                <i className='fas fa-map-marker-alt mr-1'></i>
                Locations
              </span>
            </MDBTabsItem>
          </MDBTabs>
          <MDBBtn
            type='button'
            color='primary'
            size='sm'
            onClick={() => (activeTab === 'sessions' ? handleOpenSessionModal() : handleOpenLocationModal())}
          >
            <i className='fas fa-plus mr-1'></i>
            {activeTab === 'sessions' ? 'Add Session' : 'Add Location'}
          </MDBBtn>
        </div>

        <MDBTabsContent>
          {/* Locations Tab */}
          <MDBTabsPane tabId='locations' open={activeTab === 'locations'}>
            {UniversityStore.isLoading.locations ? (
              <div className='text-center py-4'>
                <MDBSpinner />
              </div>
            ) : (
              <MDBTable responsive striped hover>
                <MDBTableHead>
                  <tr>
                    <th>Name</th>
                    <th>Address</th>
                    <th>Phone</th>
                    <th>Owner</th>
                    <th>Actions</th>
                  </tr>
                </MDBTableHead>
                <MDBTableBody>
                  {UniversityStore.locations.length === 0 ? (
                    <tr>
                      <td colSpan='5' className='text-center text-muted'>
                        No locations found. Add one to get started.
                      </td>
                    </tr>
                  ) : (
                    UniversityStore.locations.map(location => (
                      <tr key={location.id}>
                        <td>{location.name}</td>
                        <td>{formatFullAddress(location)}</td>
                        <td>{location.phone || '-'}</td>
                        <td>{getOwnerDisplayName(location)}</td>
                        <td className='location-actions'>
                          <MDBBtn
                            type='button'
                            color='info'
                            size='sm'
                            className='mr-1'
                            onClick={() => handleOpenLocationModal(location)}
                          >
                            <i className='fas fa-edit mr-1'></i>
                            Edit
                          </MDBBtn>
                          <MDBBtn type='button' color='danger' size='sm' onClick={() => handleDeleteLocation(location)}>
                            <i className='fas fa-trash mr-1'></i>
                            Delete
                          </MDBBtn>
                        </td>
                      </tr>
                    ))
                  )}
                </MDBTableBody>
              </MDBTable>
            )}
          </MDBTabsPane>

          {/* Sessions Tab */}
          <MDBTabsPane tabId='sessions' open={activeTab === 'sessions'}>
            <div className='sessions-filter-bar'>
              <UIInputToggleSwitch
                id='show-past-sessions'
                value={UniversityStore.showPastSessions}
                onChange={() => UniversityStore.toggleShowPastSessions()}
                labelOn='All Sessions'
                labelOff='Current Only'
                width={190}
              />
              <span className='filter-hint text-muted'>
                {UniversityStore.showPastSessions
                  ? 'Showing all sessions, including past and closed classes.'
                  : `Showing current sessions plus any that ended in the last 7 days.${
                      UniversityStore.pastSessionCount
                        ? ` ${UniversityStore.pastSessionCount} past ${
                            UniversityStore.pastSessionCount === 1 ? 'session is' : 'sessions are'
                          } hidden.`
                        : ''
                    }`}
              </span>
            </div>
            {UniversityStore.isLoading.sessions ? (
              <div className='text-center py-4'>
                <MDBSpinner />
              </div>
            ) : (
              <MDBTable responsive striped hover className='sessions-table'>
                <MDBTableHead>
                  <tr>
                    <th className='expand-col'></th>
                    <th>Date Range</th>
                    <th>Location</th>
                    <th>Host</th>
                    <th>Capacity</th>
                    <th>Enrolled</th>
                    <th>Actions</th>
                  </tr>
                </MDBTableHead>
                <MDBTableBody>
                  {UniversityStore.adminSessions.length === 0 ? (
                    <tr>
                      <td colSpan='7' className='text-center text-muted'>
                        {UniversityStore.pastSessionCount > 0 && !UniversityStore.showPastSessions
                          ? 'No current sessions. Switch to "All Sessions" to see past classes.'
                          : 'No sessions found. Add one to get started.'}
                      </td>
                    </tr>
                  ) : (
                    UniversityStore.adminSessions.map(session => (
                      <React.Fragment key={session.id}>
                        {/* Session Row */}
                        <tr
                          className={`${UniversityStore.expandedSessionId === session.id ? 'session-expanded' : ''} ${
                            isSessionEnded(session) ? 'session-ended' : ''
                          }`.trim()}
                        >
                          <td className='expand-col'>
                            <MDBBtn
                              type='button'
                              color='link'
                              size='sm'
                              className='expand-btn p-0'
                              onClick={() => {
                                setSelectedEnrollees([])
                                UniversityStore.toggleSessionExpand(session.id)
                              }}
                            >
                              <i
                                className={`fas fa-chevron-${
                                  UniversityStore.expandedSessionId === session.id ? 'down' : 'right'
                                }`}
                              ></i>
                            </MDBBtn>
                          </td>
                          <td>
                            {formatDateRange(session.start_date, session.end_date)}
                            {isSessionEnded(session) && (
                              <MDBBadge color='secondary' className='ml-2 session-ended-badge'>
                                Past
                              </MDBBadge>
                            )}
                            {isSessionEnded(session) && (
                              <MDBBadge color='secondary' className='ml-2 session-ended-badge'>
                                Past
                              </MDBBadge>
                            )}
                          </td>
                          <td>{session.location?.name || 'TBD'}</td>
                          <td>{session.host?.name || <em className='text-muted'>No host</em>}</td>
                          <td>{session.max_capacity || 'Unlimited'}</td>
                          <td>{session.enrolled_count || 0}</td>
                          <td className='session-actions'>
                            <MDBBtn
                              type='button'
                              color='info'
                              size='sm'
                              className='mr-1'
                              onClick={() => handleOpenSessionModal(session)}
                            >
                              <i className='fas fa-edit mr-1'></i>
                              Edit
                            </MDBBtn>
                            <MDBBtn
                              type='button'
                              color='success'
                              size='sm'
                              className='mr-1'
                              disabled={exportingSessionId === session.id}
                              onClick={() => handleExportSession(session)}
                              title='Export attendees to CSV'
                            >
                              {exportingSessionId === session.id ? (
                                <>
                                  <MDBSpinner small className='mr-1' />
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className='fas fa-file-csv mr-1'></i>
                                  Export
                                </>
                              )}
                            </MDBBtn>
                            <MDBBtn
                              type='button'
                              color='danger'
                              size='sm'
                              onClick={() => handleOpenDeleteModal(session)}
                            >
                              <i className='fas fa-trash mr-1'></i>
                              Delete
                            </MDBBtn>
                          </td>
                        </tr>

                        {/* Enrollees Expandable Section */}
                        {UniversityStore.expandedSessionId === session.id && (
                          <tr className='enrollees-row'>
                            <td colSpan='7' className='p-0'>
                              <MDBCollapse open={true}>
                                <div className='enrollees-panel'>{renderEnrolleesTable(session)}</div>
                              </MDBCollapse>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </MDBTableBody>
              </MDBTable>
            )}
          </MDBTabsPane>
        </MDBTabsContent>

        {/* Location Modal */}
        <MDBModal open={showLocationModal} onClose={handleCloseLocationModal}>
          <MDBModalDialog size='lg'>
            <MDBModalContent>
              <MDBModalHeader>
                {editingLocation ? 'Edit Location' : 'Add Location'}
                <MDBBtn type='button' className='btn-close' color='none' onClick={handleCloseLocationModal}></MDBBtn>
              </MDBModalHeader>
              <MDBModalBody>
                {locationError && <div className='alert alert-danger'>{locationError}</div>}

                <MDBRow>
                  <MDBCol size='12' md='6'>
                    <UIInput
                      id='location-name'
                      name='name'
                      label='Location Name'
                      value={locationForm.name}
                      onChange={e => setLocationForm({ ...locationForm, name: e.target.value })}
                      required
                    />
                  </MDBCol>
                  <MDBCol size='12' md='6'>
                    <UIInput
                      id='location-phone'
                      name='phone'
                      label='Phone'
                      value={locationForm.phone}
                      onChange={e => setLocationForm({ ...locationForm, phone: e.target.value })}
                    />
                  </MDBCol>
                </MDBRow>

                <MDBRow>
                  <MDBCol size='12'>
                    <UIInput
                      id='location-address-1'
                      name='address_line_1'
                      label='Street Address'
                      value={locationForm.address_line_1}
                      onChange={e =>
                        setLocationForm({
                          ...locationForm,
                          address_line_1: e.target.value,
                        })
                      }
                      required
                    />
                  </MDBCol>
                </MDBRow>

                <MDBRow>
                  <MDBCol size='12'>
                    <UIInput
                      id='location-address-2'
                      name='address_line_2'
                      label='Address Line 2 (Suite, Unit, etc.)'
                      value={locationForm.address_line_2}
                      onChange={e =>
                        setLocationForm({
                          ...locationForm,
                          address_line_2: e.target.value,
                        })
                      }
                    />
                  </MDBCol>
                </MDBRow>

                <MDBRow>
                  <MDBCol size='12' md='5'>
                    <UIInput
                      id='location-city'
                      name='city'
                      label='City'
                      value={locationForm.city}
                      onChange={e => setLocationForm({ ...locationForm, city: e.target.value })}
                      required
                    />
                  </MDBCol>
                  <MDBCol size='12' md='4'>
                    <UISelect
                      id='location-state'
                      name='state'
                      label='State'
                      value={locationForm.state}
                      options={stateOptions}
                      onChange={e => setLocationForm({ ...locationForm, state: e.target.value })}
                      search
                      required
                    />
                  </MDBCol>
                  <MDBCol size='12' md='3'>
                    <UIInput
                      id='location-zip'
                      name='zip_code'
                      label='ZIP Code'
                      value={locationForm.zip_code}
                      onChange={e =>
                        setLocationForm({
                          ...locationForm,
                          zip_code: e.target.value,
                        })
                      }
                      required
                    />
                  </MDBCol>
                </MDBRow>

                <MDBRow>
                  <MDBCol size='12'>
                    <label className='mt-3'>Owner (User)</label>
                    <UITypeAhead
                      placeholder='Search for user by name or email'
                      options={ownerSearchResults}
                      value={locationForm.owner_id}
                      onSelect={handleOwnerSelect}
                      onSearchChange={query => setOwnerSearchQuery(query)}
                      isSearching={isSearchingOwner}
                      required
                      showDropdownLimit={2}
                    />
                    {selectedOwner && <small className='text-muted'>Selected: {selectedOwner.text}</small>}
                  </MDBCol>
                </MDBRow>
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn type='button' color='secondary' onClick={handleCloseLocationModal}>
                  Cancel
                </MDBBtn>
                <MDBBtn type='button' color='primary' onClick={handleSaveLocation} disabled={locationLoading}>
                  {locationLoading ? <MDBSpinner small /> : 'Save'}
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

        {/* Session Modal */}
        <MDBModal open={showSessionModal} onClose={handleCloseSessionModal}>
          <MDBModalDialog>
            <MDBModalContent>
              <MDBModalHeader>
                {editingSession ? 'Edit Session' : 'Add Session'}
                <MDBBtn type='button' className='btn-close' color='none' onClick={handleCloseSessionModal}></MDBBtn>
              </MDBModalHeader>
              <MDBModalBody>
                {sessionError && <div className='alert alert-danger'>{sessionError}</div>}

                <div className='mb-3'>
                  <UISelect
                    id='session-location'
                    name='location_id'
                    label='Location'
                    value={sessionForm.location_id}
                    options={[
                      {
                        value: '',
                        text: '-- Select Location --',
                        checked: !sessionForm.location_id,
                      },
                      ...locationOptions,
                    ]}
                    onChange={e =>
                      setSessionForm({
                        ...sessionForm,
                        location_id: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                <MDBRow>
                  <MDBCol size='12' md='6'>
                    <UIDatePickerInput
                      id='session-start-date'
                      name='start_date'
                      label='Start Date'
                      selected={sessionForm.start_date}
                      onChange={e =>
                        setSessionForm({
                          ...sessionForm,
                          start_date: e.target.value,
                        })
                      }
                      required
                      minDate={new Date()}
                    />
                  </MDBCol>
                  <MDBCol size='12' md='6'>
                    <UIDatePickerInput
                      id='session-end-date'
                      name='end_date'
                      label='End Date'
                      selected={sessionForm.end_date}
                      onChange={e => setSessionForm({ ...sessionForm, end_date: e.target.value })}
                      required
                      minDate={sessionForm.start_date || new Date()}
                    />
                  </MDBCol>
                </MDBRow>

                <div className='mt-3'>
                  <UIInput
                    id='session-max-capacity'
                    name='max_capacity'
                    label='Max Capacity (leave blank for unlimited)'
                    type='integer'
                    value={sessionForm.max_capacity}
                    onChange={e =>
                      setSessionForm({
                        ...sessionForm,
                        max_capacity: e.target.value,
                      })
                    }
                  />
                </div>

                <div className='mt-3'>
                  <label>Session Host</label>
                  <UITypeAhead
                    placeholder='Search for host by name or email'
                    options={hostSearchResults}
                    value={sessionForm.host_id}
                    onSelect={handleHostSelect}
                    onSearchChange={query => setHostSearchQuery(query)}
                    isSearching={isSearchingHost}
                    showDropdownLimit={2}
                  />
                  {selectedHost && <small className='text-muted d-block mt-1'>Selected: {selectedHost.text}</small>}
                </div>
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn type='button' color='secondary' onClick={handleCloseSessionModal}>
                  Cancel
                </MDBBtn>
                <MDBBtn type='button' color='primary' onClick={handleSaveSession} disabled={sessionLoading}>
                  {sessionLoading ? <MDBSpinner small /> : 'Save'}
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

        {/* Delete Session Modal */}
        <MDBModal open={showDeleteModal} onClose={handleCloseDeleteModal}>
          <MDBModalDialog>
            <MDBModalContent>
              <MDBModalHeader className='delete-modal-header'>
                <i className='fas fa-exclamation-triangle text-danger mr-2'></i>
                Delete Session
                <MDBBtn type='button' className='btn-close' color='none' onClick={handleCloseDeleteModal}></MDBBtn>
              </MDBModalHeader>
              <MDBModalBody>
                <div className='delete-warning'>
                  <p>Are you sure you want to delete this session?</p>
                  {deletingSession && (
                    <p className='session-info'>
                      <strong>{formatDateRange(deletingSession.start_date, deletingSession.end_date)}</strong>
                      <br />
                      {deletingSession.location?.name || 'Location TBD'}
                    </p>
                  )}

                  {deleteEnrollmentCount.total > 0 && (
                    <div className='enrollment-warning'>
                      <div className='alert alert-warning'>
                        <i className='fas fa-users mr-2'></i>
                        This session has <strong>{deleteEnrollmentCount.total}</strong> enrolled agent(s).
                        {deleteEnrollmentCount.paid > 0 && (
                          <span>
                            {' '}
                            (<strong>{deleteEnrollmentCount.paid}</strong> have paid deposits)
                          </span>
                        )}
                      </div>

                      {deleteEnrollmentCount.paid > 0 && (
                        <div className='refund-option'>
                          <UIInputCheckbox
                            id='issue-refunds'
                            name='issue_refunds'
                            label={`Issue refunds to paid enrollees ($${UniversityStore.depositAmount} x ${deleteEnrollmentCount.paid})`}
                            checked={issueRefunds}
                            onChange={e => setIssueRefunds(e.target.checked)}
                          />
                          <small className='text-muted d-block mt-1'>
                            Refunds will be processed via Stripe to each paid agent.
                          </small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn type='button' color='secondary' onClick={handleCloseDeleteModal}>
                  Cancel
                </MDBBtn>
                <MDBBtn type='button' color='danger' onClick={handleConfirmDelete} disabled={deleteLoading}>
                  {deleteLoading ? (
                    <>
                      <MDBSpinner small className='mr-2' />
                      {issueRefunds ? 'Processing Refunds...' : 'Deleting...'}
                    </>
                  ) : (
                    <>
                      <i className='fas fa-trash mr-2'></i>
                      Delete Session
                    </>
                  )}
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

        {/* Cancel Enrollment Modal */}
        <MDBModal open={cancelModalOpen} onClose={() => setCancelModalOpen(false)}>
          <MDBModalDialog>
            <MDBModalContent>
              <MDBModalHeader>
                <i className='fas fa-user-times text-danger mr-2'></i>
                Cancel Enrollment
                <MDBBtn
                  type='button'
                  className='btn-close'
                  color='none'
                  onClick={() => setCancelModalOpen(false)}
                ></MDBBtn>
              </MDBModalHeader>
              <MDBModalBody>
                {cancellingEnrollment && (
                  <>
                    <p>
                      Cancel enrollment for <strong>{cancellingEnrollment.user?.name}</strong>?
                    </p>

                    {cancellingEnrollment.paid_at &&
                      !cancellingEnrollment.refunded_at &&
                      !cancellingEnrollment.completed_at && (
                        <div className='refund-option mt-3'>
                          <UIInputCheckbox
                            id='cancel-refund'
                            name='issue_refund'
                            label={`Issue refund of $${UniversityStore.depositAmount}`}
                            checked={issueRefundOnCancel}
                            onChange={e => setIssueRefundOnCancel(e.target.checked)}
                          />
                          <small className='text-muted d-block mt-1'>Refund will be processed via Stripe.</small>
                        </div>
                      )}
                  </>
                )}
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn type='button' color='secondary' onClick={() => setCancelModalOpen(false)}>
                  Close
                </MDBBtn>
                <MDBBtn
                  type='button'
                  color='danger'
                  onClick={handleConfirmCancel}
                  disabled={enrolleeActionLoading[cancellingEnrollment?.id]}
                >
                  {enrolleeActionLoading[cancellingEnrollment?.id] ? (
                    <>
                      <MDBSpinner small className='mr-2' />
                      Processing...
                    </>
                  ) : issueRefundOnCancel ? (
                    'Cancel & Refund'
                  ) : (
                    'Cancel Enrollment'
                  )}
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      </MDBCardBody>
    </MDBCard>
  )
})

export default AdminPanel
